import _get from 'lodash/get';
import {
  ColorEnum,
  WidgetUpdateInputObject,
  WidgetsKindEnum,
  WidgetFieldsInputObject,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';
import { transformAttachmentsForDuplication } from './transformAttachmentForDuplication';

interface LocationValue {
  name: string;
  lat: number;
  lon: number;
}

interface ButtonSettings {
  enabled: boolean;
  updatedAt: string;
}

interface OriginalButtons {
  task?: ButtonSettings;
  intervention?: ButtonSettings;
  formTemplate?: ButtonSettings;
  asset?: ButtonSettings;
  site?: ButtonSettings;
  teamMember?: ButtonSettings;
}

interface PrepareWidgetOptions {
  /** Original button settings from the backend for QuickCreation widgets */
  originalButtons?: OriginalButtons;
}

/**
 * Prepares widget data from form values for local state and preview.
 * This extracts the widget data in a format suitable for LocalWidget.
 * Attachments are passed through as-is from the form (with url for preview).
 *
 * @param values - Form values from the widget form
 * @param options - Optional settings including originalButtons for QuickCreation widgets
 */
export default function prepareWidgetFromFormValues(
  values: Values,
  options?: PrepareWidgetOptions,
): WidgetUpdateInputObject | null {
  const widgetOption = _get(values, WidgetFieldNames.Widget) as
    | SelectOption
    | undefined;

  if (!widgetOption) {
    return null;
  }

  const kind = _get(widgetOption, 'data.kind') as WidgetsKindEnum | undefined;
  const widgetTemplateId = widgetOption.value as string | undefined;

  if (!kind) {
    return null;
  }

  // Build fields based on widget type
  const fields: WidgetFieldsInputObject = {};

  switch (kind) {
    case WidgetsKindEnum.ActiveInterventions: {
      const onlyMy = _get(values, WidgetFieldNames.ShowOnlyMyInterventions);
      fields.onlyMy = Boolean(onlyMy);
      break;
    }

    case WidgetsKindEnum.Announcement: {
      // Background color
      const boxColorOption = _get(
        values,
        WidgetFieldNames.AnnouncementBoxColor,
      ) as SelectOption | undefined;
      if (boxColorOption?.value) {
        fields.backgroundColor = boxColorOption.value as ColorEnum;
      }

      // Schedule dates
      const startAt = _get(values, WidgetFieldNames.AnnouncementStartAt) as
        | string
        | undefined;
      const endAt = _get(values, WidgetFieldNames.AnnouncementEndAt) as
        | string
        | undefined;
      if (startAt) {
        fields.startAt = startAt;
      }
      if (endAt) {
        fields.endAt = endAt;
      }
      break;
    }

    case WidgetsKindEnum.Clock: {
      const timezone = _get(values, [WidgetFieldNames.Timezone, 'value']) as
        | string
        | undefined;
      if (timezone) {
        fields.timezone = timezone;
      }
      break;
    }

    case WidgetsKindEnum.ExternalLink: {
      const link = _get(values, WidgetFieldNames.ExternalUrl) as
        | string
        | undefined;
      if (link) {
        fields.link = link;
      }
      break;
    }

    case WidgetsKindEnum.WeatherToday: {
      const location = _get(
        values,
        WidgetFieldNames.WeatherTodayLocation,
      ) as LocationValue | null;
      if (location) {
        fields.location = JSON.stringify({
          name: location.name,
          lat: location.lat,
          lon: location.lon,
        });
      }
      break;
    }

    case WidgetsKindEnum.WeatherWeek: {
      const location = _get(
        values,
        WidgetFieldNames.WeatherWeekLocation,
      ) as LocationValue | null;
      if (location) {
        fields.location = JSON.stringify({
          name: location.name,
          lat: location.lat,
          lon: location.lon,
        });
      }
      break;
    }

    case WidgetsKindEnum.QuickCreation: {
      const task = Boolean(_get(values, WidgetFieldNames.QuickCreationTask));
      const intervention = Boolean(
        _get(values, WidgetFieldNames.QuickCreationIntervention),
      );
      const formTemplate = Boolean(
        _get(values, WidgetFieldNames.QuickCreationFormTemplate),
      );
      const asset = Boolean(
        _get(values, WidgetFieldNames.QuickCreationEquipment),
      );
      const site = Boolean(_get(values, WidgetFieldNames.QuickCreationSite));
      const teamMember = Boolean(
        _get(values, WidgetFieldNames.QuickCreationTeamMember),
      );

      const { originalButtons } = options || {};
      const now = new Date().toISOString();

      // Helper to get button settings - only update updatedAt if value changed
      const getButtonSettings = (
        key: keyof OriginalButtons,
        enabled: boolean,
      ): ButtonSettings => {
        const original = originalButtons?.[key];
        // If original exists and enabled value is the same, preserve original updatedAt
        if (original && original.enabled === enabled) {
          return { enabled, updatedAt: original.updatedAt };
        }
        // Value changed or no original - use current timestamp
        return { enabled, updatedAt: now };
      };

      fields.buttons = {
        task: getButtonSettings('task', task),
        intervention: getButtonSettings('intervention', intervention),
        formTemplate: getButtonSettings('formTemplate', formTemplate),
        asset: getButtonSettings('asset', asset),
        site: getButtonSettings('site', site),
        teamMember: getButtonSettings('teamMember', teamMember),
      };
      break;
    }

    // These widgets don't have additional fields
    case WidgetsKindEnum.SectorsPerformanceMetricsByDeadlinesMissed:
    case WidgetsKindEnum.SitesPerformanceMetricsByDeadlinesMissed:
    case WidgetsKindEnum.SitesMap:
    default:
      break;
  }

  // Build the widget object
  const widget: WidgetUpdateInputObject = {
    kind,
    widgetTemplateId,
    fields: Object.keys(fields).length > 0 ? fields : undefined,
  };

  // Add title, description and attachments for Announcement widget
  if (kind === WidgetsKindEnum.Announcement) {
    const title = _get(values, WidgetFieldNames.AnnouncementTitle) as
      | string
      | undefined;
    const messageContent = _get(
      values,
      WidgetFieldNames.AnnouncementMessageContent,
    ) as string | undefined;
    if (title) widget.title = title;
    if (messageContent) widget.description = messageContent;

    // Pass through raw attachments from form (with url for preview)
    // FilesUploader returns an array of files
    const imageValue = _get(values, WidgetFieldNames.AnnouncementImage) as
      | Values
      | undefined;
    if (imageValue) {
      widget.attachmentsAttached = [imageValue];
    }
  }

  if (kind === WidgetsKindEnum.ExternalLink) {
    const title = _get(values, WidgetFieldNames.ExternalLinkTitle) as
      | string
      | undefined;
    const description = _get(
      values,
      WidgetFieldNames.ExternalLinkDescription,
    ) as string | undefined;
    if (title) widget.title = title;
    if (description) widget.description = description;

    // Transform attachments from widget template (icon image) for duplication
    // These come from the widget select option data, not a form field
    // We need to transform them so the backend creates a copy of the file
    const templateAttachments = _get(
      widgetOption,
      'data.attachmentsAttached',
    ) as Values[] | undefined;
    if (templateAttachments && templateAttachments.length > 0) {
      const transformedAttachments =
        transformAttachmentsForDuplication(templateAttachments);
      if (transformedAttachments.length > 0) {
        widget.attachmentsAttached =
          transformedAttachments as WidgetUpdateInputObject['attachmentsAttached'];
      }
    }
  }

  return widget;
}
